import { useEffect, useRef } from "react";

/**
 * Lightweight intersection-based parallax.
 * Attaches a transform to the ref element based on its position in the viewport.
 * Uses rAF + passive scroll listener to stay smooth on a single render thread.
 *
 * @param {number} speed   How aggressively to translate (0.1 = subtle, 0.5 = strong)
 * @param {string} axis    "y" (default) or "x"
 */
export function useParallax(speed = 0.25, axis = "y") {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect reduced-motion preference
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;

    el.style.willChange = "transform";
    el.style.transform = "translate3d(0,0,0)";

    // Cache the reference section once — getBoundingClientRect is the only per-frame read.
    const section = el.closest("section") || el;
    let raf = 0;
    let lastY = -1;

    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // -1 (section below viewport) .. 0 (centered) .. 1 (section above viewport)
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh + rect.height);
      const shift = -progress * speed * 100; // px
      el.style.transform =
        axis === "x"
          ? `translate3d(${shift.toFixed(1)}px, 0, 0)`
          : `translate3d(0, ${shift.toFixed(1)}px, 0)`;
    };

    const onScroll = () => {
      const y = window.scrollY;
      if (raf || y === lastY) return;
      lastY = y;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (raf) cancelAnimationFrame(raf);
      el.style.willChange = "";
    };
  }, [speed, axis]);

  return ref;
}

/**
 * Adds an `data-revealed="true"` attribute to the element when it enters
 * the viewport. Pair with CSS that animates `opacity` + `transform` on reveal.
 */
export function useReveal({ threshold = 0.15, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      el.dataset.revealed = "true";
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.dataset.revealed = "true";
            io.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin]);
  return ref;
}
